import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { BuscaRemota, Selecao } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useDiscreto } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { api, mensagemDeErro } from '../../nucleo/api';
import { data as fmtData, email as fmtEmail, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE_CAIXAS_EMAIL, TIPOS_CAIXA, type CaixaEmail, type EstadoCaixas, type TipoCaixa } from './tipos';

/** U11 (lote 2): caixa que entra na tela de acesso (configura, delega ou lê por delegação). */
export const caixaComAcesso = (c: CaixaEmail) => !!(c.pode?.configurar || c.pode?.delegar || c.pode?.decidir || c.minha_delegacao_id);

/** U11 (lote 2): "pessoal de Ana" ou "corporativa" ao lado do endereço; nada na de atendimento. */
export function seloTipoCaixa(c: CaixaEmail, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  // Trajetórias (item 2): a pessoal de quem foi desligado diz que está bloqueada (ou encerrada).
  if (c.bloqueio) return `${t('u11l2.seloPessoal', { nome: nomePessoa(c.dono?.nome) || t('u11l2.semNome') })}, ${t(c.bloqueio.encerrada ? 'traj.caixa.seloEncerrada' : 'traj.caixa.selo')}`;
  if (c.tipo === 'pessoal') return t('u11l2.seloPessoal', { nome: nomePessoa(c.dono?.nome) || t('u11l2.semNome') });
  if (c.tipo === 'corporativa') return t('u11l2.seloCorporativa');
  return null;
}

type Rascunho = { tipo: TipoCaixa; dono: string | null; categorias: '' | 'ignorar' | 'trazer' };
const categoriasDe = (c: CaixaEmail, ignorar: boolean | undefined): Rascunho['categorias'] => (ignorar === undefined ? '' : ignorar === (c.tipo !== 'atendimento') ? '' : ignorar ? 'ignorar' : 'trazer');

/** Delegações de uma caixa: retirar (clique) e delegar a uma pessoa (escolhe na busca e confirma no botão). */
function Delegacoes({ c }: { c: CaixaEmail }) {
  const { t } = useTranslation('conversas');
  const qc = useQueryClient();
  const toast = useToast();
  const [pessoa, setPessoa] = useState<string | null>(null);
  const atualizar = () => qc.invalidateQueries({ queryKey: CHAVE_CAIXAS_EMAIL });
  const delegar = useMutation({
    mutationFn: () => api('POST', '/conversas/email/caixas/delegacoes', { endereco: c.endereco, usuario_id: pessoa }),
    onSuccess: () => {
      toast.sucesso(t('u11l2.delegada'));
      setPessoa(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: atualizar,
  });
  const retirar = useMutation({
    mutationFn: (id: string) => api('DELETE', `/conversas/email/delegacoes/${id}`),
    onSuccess: () => toast.sucesso(t('u11l2.retirada')),
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: atualizar,
  });
  const id = `delegar-${c.endereco.replace(/[^a-z0-9]/gi, '-')}`;
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-tinta-2">{t('u11l2.delegados')}</span>
      {c.delegados?.length ? (
        <ul className="flex flex-col gap-1">
          {c.delegados.map((d) => (
            <li key={d.id} className="flex min-w-0 items-center justify-between gap-2 text-sm text-tinta-2">
              <span className="min-w-0 break-words">{nomePessoa(d.nome) || t('u11l2.semNome')}</span>
              <Botao tamanho="sm" variante="fantasma" carregando={retirar.isPending && retirar.variables === d.id} onClick={() => retirar.mutate(d.id)} aria-label={t('u11l2.retirar', { nome: nomePessoa(d.nome) || t('u11l2.semNome') })}>
                {t('u11l2.retirarCurto')}
              </Botao>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-tinta-3">{c.tipo === 'pessoal' ? t('u11l2.semDelegadosPessoal') : t('u11l2.semDelegadosCorporativa')}</p>
      )}
      <label htmlFor={id} className="text-sm font-medium text-tinta-2">
        {t('u11l2.delegarA')}
      </label>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="min-w-0 grow basis-48">
          <BuscaRemota id={id} def={{ caminho: '/membros', rotulo: (i) => String(i.nome), pessoa: true }} valor={pessoa} aoMudar={(v) => setPessoa(v)} />
        </div>
        <Botao tamanho="sm" disabled={!pessoa} carregando={delegar.isPending} onClick={() => delegar.mutate()}>
          {t('u11l2.delegar')}
        </Botao>
      </div>
    </div>
  );
}

/**
 * Trajetórias (19/10, item 2): a caixa pessoal de quem foi desligado fica bloqueada até quem vê todas as conversas decidir:
 * transferir a outra pessoa (escolhe na busca e confirma no botão) ou encerrar (confirmação). Vale na hora.
 */
function CaixaBloqueada({ c }: { c: CaixaEmail }) {
  const { t } = useTranslation('conversas');
  const qc = useQueryClient();
  const toast = useToast();
  const [pessoa, setPessoa] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const atualizar = () => qc.invalidateQueries({ queryKey: CHAVE_CAIXAS_EMAIL });
  const decidir = useMutation({
    mutationFn: (acao: 'transferir' | 'encerrar') => api('POST', '/conversas/email/caixas/desligado', { endereco: c.endereco, acao, dono_id: acao === 'transferir' ? pessoa : null }),
    onSuccess: (_r, acao) => {
      toast.sucesso(t(acao === 'transferir' ? 'traj.caixa.transferida' : 'traj.caixa.encerradaOk'));
      setPessoa(null);
      setConfirmar(false);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: atualizar,
  });
  const id = `novo-dono-${c.endereco.replace(/[^a-z0-9]/gi, '-')}`;
  return (
    <div className="flex flex-col gap-2" data-caixa-bloqueada>
      <p className="text-sm text-tinta-2">{t('traj.caixa.bloqueada', { data: fmtData(c.bloqueio!.desde) })}</p>
      <label htmlFor={id} className="text-sm font-medium text-tinta-2">
        {t('traj.caixa.novoDono')}
      </label>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="min-w-0 grow basis-48">
          <BuscaRemota id={id} def={{ caminho: '/membros', rotulo: (i) => String(i.nome), pessoa: true }} valor={pessoa} aoMudar={(v) => setPessoa(v)} />
        </div>
        <Botao tamanho="sm" disabled={!pessoa} carregando={decidir.isPending && decidir.variables === 'transferir'} onClick={() => decidir.mutate('transferir')}>
          {t('traj.caixa.transferir')}
        </Botao>
        <Botao tamanho="sm" variante="fantasma" onClick={() => setConfirmar(true)}>
          {t('traj.caixa.encerrar')}
        </Botao>
      </div>
      <Confirmar
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        aoConfirmar={() => decidir.mutate('encerrar')}
        carregando={decidir.isPending && decidir.variables === 'encerrar'}
        titulo={t('traj.caixa.encerrarTitulo')}
        texto={t('traj.caixa.encerrarTexto')}
        perigoso
      />
    </div>
  );
}

/**
 * U11 (lote 2): tipo, dono e delegações das caixas de e-mail. Quem configura (Conversas, editar, vendo todas as empresas)
 * muda o tipo e o filtro de categorias, salvos juntos no rodapé; a caixa pessoal só muda pelo próprio dono. Delegar e
 * retirar valem na hora (botão), e quem lê por delegação pode abrir mão.
 */
export function AcessoCaixasModal({ dados, aberto, aoFechar }: { dados: EstadoCaixas; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  useDiscreto(); // Review r63 (VIS-M1): o endereço da caixa passa pelo `email()` e redesenha quando o discreto muda.
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const sessao = useSessao();
  const caixas = dados.caixas.filter(caixaComAcesso);
  const inicial = (): Record<string, Rascunho> =>
    Object.fromEntries(caixas.map((c) => [c.endereco, { tipo: c.tipo ?? 'atendimento', dono: c.dono?.id ?? null, categorias: categoriasDe(c, c.ignorar_categorias) }]));
  const [valores, setValores] = useState<Record<string, Rascunho>>(inicial);
  const mudar = (endereco: string, parcial: Partial<Rascunho>) => setValores((v) => ({ ...v, [endereco]: { ...v[endereco]!, ...parcial } }));
  const salvar = useMutation({
    mutationFn: async () => {
      for (const c of caixas) {
        if (!c.pode?.configurar) continue;
        const v = valores[c.endereco]!;
        const antes = inicial()[c.endereco]!;
        if (v.tipo === antes.tipo && v.dono === antes.dono && v.categorias === antes.categorias) continue;
        await api('PUT', '/conversas/email/caixas/acesso', {
          endereco: c.endereco,
          tipo: v.tipo,
          dono_id: v.tipo === 'pessoal' ? v.dono : null,
          ignorar_categorias: v.categorias === '' ? null : v.categorias === 'ignorar',
        });
      }
    },
    onSuccess: () => {
      toast.sucesso(t('u11l2.acessoSalvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE_CAIXAS_EMAIL }),
  });
  const abrirMao = useMutation({
    mutationFn: (id: string) => api('DELETE', `/conversas/email/delegacoes/${id}`),
    onSuccess: () => toast.sucesso(t('u11l2.retirada')),
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['conversas'] }),
  });
  const configura = caixas.some((c) => c.pode?.configurar);
  const opcoesTipo = TIPOS_CAIXA.map((x) => ({ valor: x, rotulo: t(`u11l2.tipo_${x}`) }));
  const opcoesCategorias = [
    { valor: '', rotulo: t('u11l2.categoriasPadrao') },
    { valor: 'ignorar', rotulo: t('u11l2.categoriasIgnorar') },
    { valor: 'trazer', rotulo: t('u11l2.categoriasTrazer') },
  ];
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('u11l2.acessoTitulo')}
      rodape={
        configura ? (
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
              {tn('comum.salvar')}
            </Botao>
          </>
        ) : (
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('u11l2.acessoTexto')}</p>
        {caixas.map((c) => {
          const v = valores[c.endereco]!;
          const souDono = c.tipo === 'pessoal' && c.dono?.id === sessao.usuario.id;
          const idBase = c.endereco.replace(/[^a-z0-9]/gi, '-');
          return (
            <section key={c.endereco} className="flex min-w-0 flex-col gap-3 rounded-xl border border-borda p-3" aria-label={fmtEmail(c.endereco)}>
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                <span className="min-w-0 font-mono text-sm break-all text-tinta-1">{fmtEmail(c.endereco)}</span>
                {seloTipoCaixa(c, t) && <span className="text-xs text-tinta-3">({seloTipoCaixa(c, t)})</span>}
              </div>
              {c.pode?.configurar ? (
                <>
                  <label className="flex min-w-0 flex-col gap-1.5 text-sm font-medium text-tinta-2">
                    {t('u11l2.tipo')}
                    <Selecao opcoes={opcoesTipo} value={v.tipo} onChange={(e) => mudar(c.endereco, { tipo: e.target.value as TipoCaixa })} />
                  </label>
                  {v.tipo === 'pessoal' && (
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <label htmlFor={`dono-${idBase}`} className="text-sm font-medium text-tinta-2">
                        {t('u11l2.dono')}
                      </label>
                      <BuscaRemota id={`dono-${idBase}`} def={{ caminho: '/membros', rotulo: (i) => String(i.nome), pessoa: true }} valor={v.dono} rotuloAtual={c.dono?.nome ?? undefined} aoMudar={(x) => mudar(c.endereco, { dono: x })} />
                      {!souDono && <span className="text-xs text-tinta-3">{t('u11l2.avisoPessoal')}</span>}
                    </div>
                  )}
                  <label className="flex min-w-0 flex-col gap-1.5 text-sm font-medium text-tinta-2">
                    {t('u11l2.categorias')}
                    <Selecao opcoes={opcoesCategorias} value={v.categorias} onChange={(e) => mudar(c.endereco, { categorias: e.target.value as Rascunho['categorias'] })} />
                  </label>
                </>
              ) : (
                c.tipo === 'pessoal' && !souDono && !c.bloqueio && <p className="text-sm text-tinta-3">{t('u11l2.soDono')}</p>
              )}
              {c.pode?.decidir && c.bloqueio && <CaixaBloqueada c={c} />}
              {c.bloqueio?.encerrada && <p className="text-sm text-tinta-3">{t('traj.caixa.encerrada')}</p>}
              {c.pode?.delegar && <Delegacoes c={c} />}
              {c.minha_delegacao_id && (
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-tinta-3">{t('u11l2.porDelegacao')}</span>
                  <Botao tamanho="sm" variante="fantasma" carregando={abrirMao.isPending} onClick={() => abrirMao.mutate(c.minha_delegacao_id!)}>
                    {t('u11l2.abrirMao')}
                  </Botao>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}
